// src/components/portal/StudentProgressModal.js
// Renders the same data as GenerateStudentWordReport but inline in a React modal.
// Opened when a student's name is clicked in the portal dashboards.
import React, { useState, useEffect, useCallback } from "react";
import { getFunctions } from "firebase/functions";
import { httpsCallable } from "../../shared/legacyData/legacyCallable";
import { fetchAIContentBuilderData, buildUnitId } from "../learningDashboard/components/contentHelpers/contentFetch";
import { fetchProgramLearnerProgressUnitsMap } from "../learningDashboard/components/learningProgressSnapshotUtils";
import "./Portal.css";

const COMPANY          = "TPC_Group";
const COLLECTION_BRAND = "TechPerformanceCentre";
const programmeStructureCache = new Map();
const unitAssessmentCache = new Map();

// ─── C/NYC helpers ────────────────────────────────────────────────────────────

const getPR     = (ps) => Number(ps?.passRate || ps?.data?.passRate || 70);
const getCNYC   = (written, total, avg, pr = 70, belowPassCount = 0) => {
if (total === 0 || written === 0) return "NYC-NS";
if (written < total) return "IN_PROGRESS";
if (belowPassCount > 0) return "NYC-BP";
if (avg >= pr) return "C";
return "NYC-BP";
};
const cnycLabel = (s) => ({ "C": "Competent", "NYC-NS": "NYC — Not Submitted", "NYC-BP": "Not Complete" })[s] || "NYC";
const cnycDeclClass = (s) => ({ "C": "portal-cnyc-decl--c", "NYC-NS": "portal-cnyc-decl--nyc-ns", "NYC-BP": "portal-cnyc-decl--nyc-bp" })[s] || "portal-cnyc-decl--nyc-ns";
const cnycColor = (s) => ({ "C": "var(--p-green)", "NYC-NS": "var(--p-grey)", "NYC-BP": "var(--p-red)" })[s] || "var(--p-grey)";
const declarationLabel = (s) => s === "IN_PROGRESS" ? "In progress" : cnycLabel(s);
const declarationClass = (s) => s === "IN_PROGRESS" ? "portal-cnyc-decl--in-progress" : cnycDeclClass(s);
const declarationColor = (s) => s === "IN_PROGRESS" ? "#92400E" : cnycColor(s);
const pctColor  = (v) => v >= 70 ? "var(--p-green)" : v >= 50 ? "var(--p-amber)" : "var(--p-red)";
const fmtPct    = (v) => `${Math.round(v || 0)}%`;
const capProgress = (written, total) => {
const safeTotal = Math.max(0, Number(total) || 0);
const safeWritten = Math.max(0, Number(written) || 0);
return safeTotal > 0 ? Math.min(safeWritten, safeTotal) : safeWritten;
};
const progressText = (written, total) => Number(total || 0) > 0 ? `${capProgress(written, total)}/${total}` : "-";
const progressStatusLabel = (written, total, avg, passRate = 70) => {
if (!written) return "NYC - Not submitted";
if (total > 0 && written < total) return "In progress";
if (avg >= passRate) return "Completed";
return "Not Complete";
};
const cnycStatusLabel = (status) => {
if (status === "C") return "Competent";
if (status === "IN_PROGRESS") return "In progress";
if (status === "NYC-BP") return "Not Complete";
return "NYC - Not submitted";
};
const completedAssessmentCount = (item) => (
capProgress(Number(item?.written || 0), item?.total)
);
const progressStatusStyle = (status) => {
if (status === "Completed" || status === "Competent") return { background: "var(--p-green-bg)", color: "var(--p-green)" };
if (status === "In Progress" || status === "In progress") return { background: "#FEF3C7", color: "#92400E" };
return { background: "var(--p-red-bg)", color: "var(--p-red)" };
};
const avgCellStyle = (avg, hasProgress) => ({
textAlign: "center",
fontWeight: 800,
fontSize: 12,
color: hasProgress && avg >= 70 ? "var(--p-green)" : "var(--p-red)",
background: hasProgress && avg >= 70 ? "var(--p-green-bg)" : "var(--p-red-bg)",
});

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, borderColor, bgColor }) {
return (
<div className="portal-stat-card" style={{ borderLeftColor: borderColor || "var(--p-navy)", background: bgColor || "var(--p-lgrey)" }}>
<div className="portal-stat-card__label">{label}</div>
<div className="portal-stat-card__value" style={{ color: borderColor || "var(--p-navy)", fontSize: 22 }}>{value}</div>
{sub && <div className="portal-stat-card__sub">{sub}</div>}
</div>
);
}

// ─── Main modal

function ReportInfoGrid({ rows }) {
return (
<div style={{ border: "1px solid var(--p-border)", borderRadius: 8, overflow: "hidden", marginBottom: 12 }}>
{(rows || []).map(([label, value], idx) => (
<div
    key={`${label}-${idx}`}
    style={{
    display: "grid",
    gridTemplateColumns: "180px 1fr",
    borderTop: idx === 0 ? "none" : "1px solid var(--p-border)",
    background: idx % 2 === 0 ? "#fff" : "var(--p-lgrey)",
    }}
>
    <div style={{ padding: "10px 12px", fontSize: 12, fontWeight: 800, color: "var(--p-navy)", textTransform: "uppercase", letterSpacing: 0 }}>
    {label}
    </div>
    <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--p-dark)", wordBreak: "break-word" }}>
    {value || "-"}
    </div>
</div>
))}
</div>
);
}

export default function StudentProgressModal({ student, funder, allIntakeBlocks = [], onClose }) {
const [loading,    setLoading]    = useState(true);
const [data,       setData]       = useState(null);
const [error,      setError]      = useState("");
const [refreshing, setRefreshing] = useState(false);

const loadStudentData = useCallback(async (isRefresh = false) => {
if (!student) return;
if (isRefresh) {
setRefreshing(true);
} else {
const instantData = buildInstantStudentData(student, allIntakeBlocks);
setData(instantData);
setLoading(false);
setError("");
}
try {
const intakeBlocks = allIntakeBlocks.length ? allIntakeBlocks : await fetchIntakeBlocksFromFirestore();
const resolvedProgramme = resolveProgrammeForStudent(student, intakeBlocks);
const programmeId = resolvedProgramme?.programmeId || student.programmeId || "";
const intakeBlock = resolvedProgramme?.intakeBlock || null;

// Fetch programme structure if we have a programmeId
let ps  = null;
let uac = {};
if (programmeId) {
ps = await fetchProgrammeStructureCached(programmeId);
}

if (ps?.modules && programmeId) {
uac = await fetchUnitAssessmentsForProgram(ps, programmeId);
}

// Fetch scores directly from learnerAssessments/{email}/units/.../outline/...
// This is the authoritative path — assessmentStory may lag behind or be incomplete.
const unitIds    = buildProgrammeUnitIds(ps, programmeId);
const learnerIds = buildLearnerLookupIds(student);
const learningProgressUnits = unitIds.length
    ? await fetchProgramLearnerProgressUnitsMap(programmeId, learnerIds, unitIds, "fetchLearnerIntakeStructurePreviewV2")
    : {};
const snapshotGradeMap = buildGradeMapFromSnapshot(learningProgressUnits);
const snapshotHistory = buildHistoryFromSnapshot(learningProgressUnits);
const snapshotAssessmentCache = buildAssessmentCacheFromSnapshot(learningProgressUnits);
if (Object.keys(snapshotAssessmentCache).length > 0) uac = { ...uac, ...snapshotAssessmentCache };

// Build history and aggregates
const history   = snapshotHistory;
const passRate  = getPR(ps);
const submitted = history.filter((e) => e?.assessment && pctFromEntry(e) != null);
const written   = submitted.length;
const rawSum    = submitted.reduce((s, e) => s + pctFromEntry(e), 0);
const currentAvg = written > 0 ? Math.round(rawSum / written) : 0;

// Build failed assessments list
const seenTitles = new Set();
const failedAssessments = [];
submitted.forEach((e) => {
const name = e.sectionTitle || e.outcomeName || "";
const pct  = pctFromEntry(e);
if (!name || pct >= passRate) return;
if (!seenTitles.has(name)) {
    seenTitles.add(name);
    failedAssessments.push({ name, score: Math.round(pct), passRate });
}
});

const studentWithStory = { ...student, assessmentStory: { complexityHistory: history } };
const agg = ps?.modules ? buildStudentAggregatesWithContent(studentWithStory, ps, uac, snapshotGradeMap) : null;
const byModule = agg?.byModule || {};
const byUnit = agg?.byUnit || {};
const reportFailedAssessments = agg?.failedAssessments || failedAssessments;
const overallTotal = ps?.modules ? Object.values(byModule).reduce((s, m) => s + (m.total || 0), 0) : written;
const overallSubmitted = capProgress(ps?.modules ? Object.values(byModule).reduce((s, m) => s + (m.written || 0), 0) : written, overallTotal);
const overallWritten = capProgress(ps?.modules ? Object.values(byUnit).reduce((s, u) => s + completedAssessmentCount(u), 0) : written, overallTotal);
const overallRawSum = ps?.modules ? Object.values(byModule).reduce((s, m) => s + (m.rawSum || 0), 0) : rawSum;
const overallBelowPassCount = ps?.modules ? Object.values(byModule).reduce((s, m) => s + (m.belowPassCount || 0), 0) : reportFailedAssessments.length;
const reportCurrentAvg = overallSubmitted > 0 ? Math.min(100, Math.round(overallRawSum / overallSubmitted)) : currentAvg;
const courseName = student.intakeLabel || intakeBlock?.label || student.programmeName || ps?.name || ps?.programName || programmeId || "-";
const unitProgressRows = buildUnitProgressRows(ps, byUnit, courseName);
const outcomeOutlineRows = ps?.modules ? buildOutcomeOutlineRows(ps, uac, history, byUnit, snapshotGradeMap) : [];
// unitIds, learnerIds, learningProgressUnits already fetched above before aggregates
const learningUnitProgress = summarizeLearningProgressUnits(learningProgressUnits, unitIds);
const aggregateUnitProgressTotal = countProgrammeUnits(ps) || unitProgressRows.length || (overallTotal > 0 ? 1 : 0);
const aggregateUnitProgressDone = unitProgressRows.filter((row) => Number(row.total || 0) > 0 && Number(row.written || 0) >= Number(row.total || 0) && Number(row.belowPassCount || 0) === 0).length;
const unitProgressTotal = learningUnitProgress.total || aggregateUnitProgressTotal;
const unitProgressDone = learningUnitProgress.total ? learningUnitProgress.done : aggregateUnitProgressDone;
const unitProgressComplete = unitProgressTotal > 0 && unitProgressDone >= unitProgressTotal;
const assessmentProgressComplete = overallTotal > 0 && overallWritten >= overallTotal;
const overallCNYC = unitProgressComplete && assessmentProgressComplete
? getCNYC(overallWritten, overallTotal, reportCurrentAvg, passRate, overallBelowPassCount)
: "IN_PROGRESS";
const nycUnitRows = unitProgressRows.filter((row) => row.cnycStatus !== "C" || Number(row.belowPassCount || 0) > 0);
const resubmissionRows = buildResubmissionRows(outcomeOutlineRows, reportFailedAssessments, passRate);

setData({
written: overallWritten,
submittedWritten: overallSubmitted,
overallTotal,
currentAvg: reportCurrentAvg,
passRate,
overallCNYC,
byModule,
byUnit,
unitProgressRows,
outcomeOutlineRows,
unitProgressDone,
unitProgressTotal,
nycUnitRows,
resubmissionRows,
overallBelowPassCount,
failedAssessments: reportFailedAssessments,
ps,
programmeId,
intakeBlock,
});
} catch (err) {
console.error("[StudentProgressModal] error:", err);
setError("Could not load student progress data. Please try again.");
} finally {
setLoading(false);
setRefreshing(false);
}
}, [student, allIntakeBlocks]);

useEffect(() => {
if (!student) return;
loadStudentData();
// Prevent background scroll
document.body.style.overflow = "hidden";
return () => { document.body.style.overflow = ""; };
}, [student, loadStudentData]);

if (!student) return null;

return (
<div className="portal-modal-overlay" onClick={onClose}>
<div className="portal-modal" onClick={(e) => e.stopPropagation()}>
{/* Header */}
<div className="portal-modal__header">
    <div>
    <p className="portal-modal__title">{student.fullName || student.email}</p>
    <p className="portal-modal__subtitle">{student.email} {student.programmeId ? `· ${student.programmeId}` : ""}</p>
    </div>
    <button className="portal-modal__close" onClick={onClose} title="Close">×</button>
</div>

{/* Body */}
<div className="portal-modal__body">
    {loading && (
    <div className="portal-loading">
        <div className="portal-loading__spinner" />
        Loading progress data…
    </div>
    )}

    {error && !loading && (
    <div style={{ background: "var(--p-red-bg)", color: "var(--p-red)", borderRadius: 8, padding: "14px 18px", fontSize: 14 }}>
        {error}
    </div>
    )}

    {data && !loading && (
    <>
        {data.isInstantSnapshot && (
        <div style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", borderRadius: 8, padding: "10px 14px", fontSize: 12, fontWeight: 700, marginBottom: 14 }}>
            Showing dashboard snapshot while detailed module progress loads.
        </div>
        )}

        {/* Stat cards */}
        <div className="portal-stat-row" style={{ gridTemplateColumns: "repeat(3, 1fr)", marginBottom: 16 }}>
        <StatCard
            label="Current Average"
            value={data.submittedWritten > 0 ? fmtPct(data.currentAvg) : "No data"}
            sub="completed assessments only"
            borderColor={pctColor(data.currentAvg)}
            bgColor={data.currentAvg >= data.passRate ? "var(--p-green-bg)" : "var(--p-red-bg)"}
        />
        <StatCard
            label="Assessment Progress"
            value={progressText(data.written, data.overallTotal)}
            sub="assessments completed"
            borderColor="var(--p-navy)"
        />
        <StatCard
            label="Pass Rate"
            value={`${data.passRate}%`}
            sub="programme minimum"
            borderColor="var(--p-grey)"
            bgColor="var(--p-lgrey)"
        />
        </div>

        {/* Overall C/NYC declaration */}
        <div className={`portal-cnyc-decl ${declarationClass(data.overallCNYC)}`} style={{ marginBottom: 20 }}>
        <p className="portal-cnyc-decl__label" style={{ color: declarationColor(data.overallCNYC) }}>
            Overall Declaration: {declarationLabel(data.overallCNYC)}
        </p>
        <p className="portal-cnyc-decl__sub">
            {data.overallCNYC === "C"
            ? `All assessments completed and every assessment is at or above the ${data.passRate}% pass rate.`
            : data.overallCNYC === "NYC-BP"
                ? `${data.overallBelowPassCount || data.resubmissionRows?.length || 0} assessment(s) are below the ${data.passRate}% pass rate. See resubmissions below.`
                : data.overallCNYC === "IN_PROGRESS"
                ? `Learner is still in progress: ${progressText(data.written, data.overallTotal)} assessments completed.`
                : data.submittedWritten === 0
                ? "No assessments submitted yet."
                : `${Math.max(0, data.overallTotal - data.written)} of ${data.overallTotal} assessments not yet completed competently.`}
        </p>
        </div>

        <div className="portal-report-preview" style={{ marginBottom: 20 }}>
        <p className="portal-modal__section-title">Student Progress Report Preview</p>
        <ReportInfoGrid
            rows={[
            ["Learner", student.fullName || student.name || student.email || "-"],
            ["Email", student.email || "-"],
            ["Student Number", student.studentNumber || student.studentNo || student.learnerNumber || "-"],
            ["Programme", student.intakeLabel || data.intakeBlock?.label || student.programmeName || data.programmeId || "-"],
            ["Programme Code", data.ps?.code || data.ps?.programCode || data.programmeId || "-"],
            ["NQF Level", data.ps?.nqfLevel || data.ps?.data?.nqfLevel || "-"],
            ["Report Date", new Date().toLocaleDateString("en-ZA")],
            ]}
        />
        <p className="portal-modal__section-title" style={{ marginTop: 16 }}>Progress Summary</p>
        <ReportInfoGrid
            rows={[
            ["Current Average", data.submittedWritten > 0 ? fmtPct(data.currentAvg) : "No data"],
            ["Assessment Progress", progressText(data.written, data.overallTotal)],
            ["Unit Progress", data.unitProgressTotal > 0 ? `${data.unitProgressDone}/${data.unitProgressTotal}` : "-"],
            ["Pass Rate", `${data.passRate}%`],
            ["C / NYC", declarationLabel(data.overallCNYC)],
            ]}
        />
        {funder && (
            <>
            <p className="portal-modal__section-title" style={{ marginTop: 16 }}>Funder Details</p>
            <ReportInfoGrid
                rows={[
                ["Funder Name", funder.FunderName || funder.funderName || "-"],
                ["Funder ID", funder.id || "-"],
                ["Contact Officer", funder.ContactOfficer || funder.contactOfficer || funder.contactPerson || "-"],
                ["Email", funder.ContactOfficerEmail || funder.contactOfficerEmail || funder.contactEmail || funder.email || "-"],
                ]}
            />
            </>
        )}
        </div>

        {/* Module and unit report tables */}
        {data.ps?.modules?.length > 0 && (
        <>
            <p className="portal-modal__section-title" style={{ textTransform: "none", fontSize: 18, letterSpacing: 0 }}>Module Summary</p>
            <table className="portal-prog-table" style={{ marginBottom: 20 }}>
            <thead>
                <tr>
                <th>Module</th>
                <th style={{ textAlign: "center" }}>Current Avg</th>
                <th style={{ textAlign: "center" }}>Progress</th>
                <th style={{ textAlign: "center" }}>C / NYC</th>
                </tr>
            </thead>
            <tbody>
                {data.ps.modules.map((mod) => {
                const m = data.byModule[mod.name] || { currentAvg: 0, written: 0, total: 0, cnycStatus: "NYC-NS" };
                const written = (m.units || []).reduce((sum, unitName) => sum + completedAssessmentCount(data.byUnit[unitName]), 0);
                return (
                    <tr key={mod.name}>
                    <td>{mod.name}</td>
                    <td style={avgCellStyle(m.currentAvg, m.written > 0)}>{fmtPct(m.currentAvg)}</td>
                    <td style={{ textAlign: "center" }}>{progressText(written, m.total)}</td>
                    <td style={{ textAlign: "center", fontSize: 12, fontWeight: 700, ...progressStatusStyle(cnycStatusLabel(m.cnycStatus)) }}>
                    {cnycStatusLabel(m.cnycStatus)}
                    </td>
                    </tr>
                );
                })}
            </tbody>
            </table>

            {data.ps.modules.map((mod, moduleIndex) => {
            const m = data.byModule[mod.name];
            const moduleOutcomeRows = (data.outcomeOutlineRows || []).filter((row) => row.moduleName === mod.name);
            if (!m?.units?.length) return null;
            return (
                <div key={mod.name} style={{ marginBottom: 20 }}>
                <p className="portal-modal__section-title" style={{ textTransform: "none", fontSize: 15, letterSpacing: 0 }}>
                    Module {moduleIndex + 1}: {mod.name}
                </p>
                <table className="portal-prog-table">
                    <thead>
                    <tr>
                        <th>Unit</th>
                        <th style={{ textAlign: "center" }}>Current Avg</th>
                        <th style={{ textAlign: "center" }}>Progress</th>
                        <th style={{ textAlign: "center" }}>Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    {m.units.map((unitName) => {
                        const u = data.byUnit[unitName] || { currentAvg: 0, written: 0, total: 0, cnycStatus: "NYC-NS" };
                        const written = completedAssessmentCount(u);
                        const status = cnycStatusLabel(u.cnycStatus);
                        return (
                        <tr key={unitName}>
                            <td style={{ fontSize: 12 }}>{unitName}</td>
                            <td style={avgCellStyle(u.currentAvg, u.written > 0)}>{fmtPct(u.currentAvg)}</td>
                            <td style={{ textAlign: "center", fontSize: 12 }}>{progressText(written, u.total)}</td>
                            <td style={{ textAlign: "center", fontSize: 12, fontWeight: 700, ...progressStatusStyle(status) }}>
                            {status}
                            </td>
                        </tr>
                        );
                    })}
                    </tbody>
                </table>
                {m.units.map((unitName) => {
                const rows = moduleOutcomeRows.filter((row) => row.unitName === unitName);
                if (!rows.length) return null;
                return (
                    <div key={`${mod.name}-${unitName}-outlines`} style={{ marginTop: 12, marginBottom: 16 }}>
                    <p style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 800, color: "var(--p-navy)" }}>
                        {unitName} - Outcome & Outline Progress
                    </p>
                    <table className="portal-prog-table">
                        <thead>
                        <tr>
                            <th>Module</th>
                            <th>Component</th>
                            <th>Unit</th>
                            <th>Outcome</th>
                            <th>Outline</th>
                            <th style={{ textAlign: "center" }}>Current Avg</th>
                            <th style={{ textAlign: "center" }}>Progress</th>
                            <th style={{ textAlign: "center" }}>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((row, rowIndex) => {
                            const written = capProgress(row.written, row.total);
                            const status = progressStatusLabel(written, row.total, row.currentAvg, data.passRate);
                            return (
                            <tr key={`${row.unitName}-${row.outlineName}-${rowIndex}`}>
                                <td style={{ fontSize: 12 }}>{row.moduleName || "-"}</td>
                                <td style={{ fontSize: 12 }}>{row.componentName || "-"}</td>
                                <td style={{ fontSize: 12 }}>{row.unitName || "-"}</td>
                                <td style={{ fontSize: 12 }}>{row.outcomeName}</td>
                                <td style={{ fontSize: 12 }}>{row.outlineName}</td>
                                <td style={avgCellStyle(row.currentAvg, row.written > 0)}>{fmtPct(row.currentAvg)}</td>
                                <td style={{ textAlign: "center", fontSize: 12 }}>{progressText(written, row.total)}</td>
                                <td style={{ textAlign: "center", fontSize: 12, fontWeight: 700, ...progressStatusStyle(status) }}>
                                {status}
                                </td>
                            </tr>
                            );
                        })}
                        </tbody>
                    </table>
                    </div>
                );
                })}
                </div>
            );
            })}
        </>
        )}

        {/* NYC units */}
        {data.nycUnitRows?.length > 0 && (
        <>
            <p className="portal-modal__section-title" style={{ color: "var(--p-red)" }}>
            Units Currently NYC ({data.nycUnitRows.length})
            </p>
            <table className="portal-prog-table" style={{ marginBottom: 20 }}>
            <thead>
                <tr>
                <th>Module</th>
                <th>Component</th>
                <th>Unit</th>
                <th style={{ textAlign: "center" }}>Current Avg</th>
                <th style={{ textAlign: "center" }}>Progress</th>
                <th style={{ textAlign: "center" }}>Declaration</th>
                </tr>
            </thead>
            <tbody>
                {data.nycUnitRows.map((row, i) => (
                <tr key={`${row.unitName}-${i}`}>
                    <td style={{ fontSize: 12 }}>{row.moduleName || "-"}</td>
                    <td style={{ fontSize: 12 }}>{row.componentName || "-"}</td>
                    <td style={{ fontSize: 12 }}>{row.unitName}</td>
                    <td style={avgCellStyle(row.currentAvg, row.written > 0)}>{row.written > 0 ? fmtPct(row.currentAvg) : "-"}</td>
                    <td style={{ textAlign: "center", fontSize: 12 }}>{progressText(completedAssessmentCount(row), row.total)}</td>
                    <td style={{ textAlign: "center" }}>
                    <span className={`portal-badge ${row.cnycStatus === "IN_PROGRESS" ? "portal-badge--in-progress" : row.cnycStatus === "NYC-BP" ? "portal-badge--nyc-bp" : "portal-badge--nyc-ns"}`} style={{ fontSize: 10 }}>
                        {cnycStatusLabel(row.cnycStatus)}
                    </span>
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </>
        )}

        {/* Assessments requiring resubmission */}
        {data.resubmissionRows?.length > 0 && (
        <>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                <p className="portal-modal__section-title" style={{ color:"var(--p-red)", margin:0 }}>
                Tests To Redo Or Resubmit ({data.resubmissionRows.length})
                </p>
                <button
                type="button"
                onClick={() => loadStudentData(true)}
                disabled={refreshing}
                style={{
                    display:"flex", alignItems:"center", gap:6,
                    padding:"5px 12px", borderRadius:7,
                    border:"1.5px solid var(--p-navy)",
                    background: refreshing ? "var(--p-lgrey)" : "var(--p-white)",
                    color:"var(--p-navy)", fontSize:12, fontWeight:700,
                    cursor: refreshing ? "not-allowed" : "pointer",
                    opacity: refreshing ? 0.6 : 1,
                }}
                title="Reload assessment data to reflect recent resubmissions"
                >
                {refreshing ? "Refreshing…" : "↺ Refresh"}
                </button>
            </div>
            <p style={{ fontSize:13, color:"var(--p-grey)", marginBottom:10 }}>
                The following were submitted but did not meet the {data.passRate}% pass rate.
                {refreshing && <span style={{ marginLeft:8, color:"var(--p-navy)", fontWeight:700 }}>Checking for updates…</span>}
            </p>
            <table className="portal-prog-table">
            <thead>
                <tr>
                <th>Module</th>
                <th>Component</th>
                <th>Unit</th>
                <th>Outcome</th>
                <th>Outline</th>
                <th style={{ textAlign: "center" }}>Score</th>
                <th style={{ textAlign: "center" }}>Pass Rate</th>
                <th style={{ textAlign: "center" }}>Status</th>
                </tr>
            </thead>
            <tbody>
                {data.resubmissionRows.map((fa, i) => (
                <tr key={`${fa.unitName || "assessment"}-${fa.name}-${i}`}>
                    <td style={{ fontSize: 12 }}>{fa.moduleName || "-"}</td>
                    <td style={{ fontSize: 12 }}>{fa.componentName || "-"}</td>
                    <td style={{ fontSize: 12 }}>{fa.unitName || "-"}</td>
                    <td style={{ fontSize: 12 }}>{fa.outcomeName || "-"}</td>
                    <td style={{ fontSize: 12 }}>{fa.outlineName || fa.name}</td>
                    <td style={{ textAlign:"center", fontWeight:700, fontSize:12,
                        color: fa.score >= fa.passRate ? "var(--p-green)" : fa.score >= 50 ? "var(--p-amber)" : "var(--p-red)",
                        background: fa.score >= fa.passRate ? "var(--p-green-bg)" : "var(--p-red-bg)",
                    }}>{fa.score}%</td>
                    <td style={{ textAlign:"center", fontSize:12 }}>{fa.passRate}%</td>
                    <td style={{ textAlign:"center" }}>
                        {fa.score >= fa.passRate ? (
                        <span className="portal-badge portal-badge--c" style={{ fontSize:10 }}>Completed ✓</span>
                        ) : (
                        <span className="portal-badge portal-badge--nyc-bp" style={{ fontSize:10 }}>
                            {fa.score === 0 ? "Not Submitted" : "Needs Resubmission"}
                        </span>
                        )}
                    </td>
                </tr>
                ))}
            </tbody>
            </table>
        </>
        )}

        {/* No data state */}
        {data.submittedWritten === 0 && (
        <div className="portal-empty">
            This learner has not submitted any assessments yet.
        </div>
        )}
    </>
    )}
</div>
</div>
</div>
);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const rowArray = (result) => {
const data = result?.data?.data;
if (Array.isArray(data)) return data;
if (data && typeof data === "object") return Object.values(data);
return [];
};

async function fetchIntakeBlocksFromFirestore() {
try {
const getSystemDocuments = httpsCallable(getFunctions(), "getSystemDocuments");
const result = await getSystemDocuments({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "intakeBlocks", auth: true });
const rows = rowArray(result);
return rows.map((block, index) => ({
...block,
id: String(block?.id || block?.uid || block?.uniqueId || block?.intakeId || block?.docId || `intake-${index}`),
uid: String(block?.uid || block?.id || block?.uniqueId || ""),
label: String(block?.label || block?.name || block?.intakeName || block?.id || `Intake ${index + 1}`),
programmeId: String(block?.programme || block?.program || block?.programmeId || block?.programId || "").trim(),
}));
} catch {
return [];
}
}

function getStudentIntakeIds(student) {
const fromAssignment = Object.entries(student?.intakeAssignment || {})
.filter(([, enabled]) => Boolean(enabled))
.map(([id]) => id);
const fromSelected = Array.isArray(student?.selectedIntakeBlockIds) ? student.selectedIntakeBlockIds : [];
const fromSingle = student?.selectedIntakeBlockId ? [student.selectedIntakeBlockId] : [];
return Array.from(new Set([...fromAssignment, ...fromSelected, ...fromSingle].map((id) => String(id || "").trim()).filter(Boolean)));
}

function resolveProgrammeForStudent(student, intakeBlocks) {
const intakeIds = getStudentIntakeIds(student).map((id) => id.toLowerCase());
for (const intakeId of intakeIds) {
const block = (intakeBlocks || []).find((item) => {
const candidates = [item?.id, item?.uid, item?.uniqueId, item?.intakeId, item?.docId, item?.label, item?.name, item?.intakeName]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);
return candidates.includes(intakeId);
});
const programmeId = String(block?.programmeId || block?.programme || block?.program || block?.programId || "").trim();
if (programmeId) return { programmeId, intakeBlock: block };
}
const fallback = String(student?.programmeId || student?.programId || student?.programme || "").trim();
return fallback ? { programmeId: fallback, intakeBlock: null } : null;
}

function buildInstantStudentData(student, allIntakeBlocks = []) {
const resolvedProgramme = resolveProgrammeForStudent(student, allIntakeBlocks);
const programmeId = resolvedProgramme?.programmeId || student?.programmeId || "";
const total = Number(student?.overallTotal ?? student?.totalAssessments ?? student?.unitProgressTotal ?? 0);
const written = capProgress(
Number(student?.overallWritten ?? student?.written ?? student?.completedAssessments ?? student?.unitProgressDone ?? 0),
total,
);
const submittedWritten = capProgress(Number(student?.overallSubmitted ?? student?.submittedWritten ?? student?.written ?? 0), total);
const currentAvg = Number(student?.currentAvg ?? student?.overallAvg ?? 0);
const passRate = Number(student?.passRate ?? student?.agg?.passRate ?? 70);
const unitProgressDone = Number(student?.unitProgressDone ?? 0);
const unitProgressTotal = Number(student?.unitProgressTotal ?? 0);
const belowPassCount = currentAvg > 0 && currentAvg < passRate && written >= total ? 1 : 0;
return {
written,
submittedWritten,
overallTotal: total,
currentAvg,
passRate,
overallCNYC: student?.cnycStatus || getCNYC(written, total, currentAvg, passRate, belowPassCount),
byModule: {},
byUnit: {},
unitProgressRows: [],
outcomeOutlineRows: [],
unitProgressDone,
unitProgressTotal,
nycUnitRows: [],
resubmissionRows: [],
overallBelowPassCount: belowPassCount,
failedAssessments: [],
ps: null,
programmeId,
intakeBlock: resolvedProgramme?.intakeBlock || null,
isInstantSnapshot: true,
};
}

async function fetchProgrammeStructureCached(programmeId) {
if (!programmeId) return null;
if (!programmeStructureCache.has(programmeId)) {
programmeStructureCache.set(programmeId, (async () => {
const gsd = httpsCallable(getFunctions(), "getSystemDocuments");
const programmeSources = [];
try {
    const psRes = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "programStructure", uniqueId: programmeId, auth: true });
    if (psRes?.data?.data) programmeSources.push(psRes.data.data);
} catch { /* try programme details source below */ }
try {
    const getProgramDetails = httpsCallable(getFunctions(), "getProgramDetails");
    const detailsRes = await getProgramDetails({ programId: programmeId, company: COMPANY });
    if (detailsRes?.data?.success && detailsRes.data.data) programmeSources.push(detailsRes.data.data);
} catch { /* no programme details */ }
return selectBestProgramStructure(programmeSources);
})());
}
return programmeStructureCache.get(programmeId);
}

const addStoryKey = (map, key, story) => {
const normalized = String(key || "").trim().toLowerCase();
if (normalized && !map.has(normalized)) map.set(normalized, story);
};

function buildStoryMap(stories) {
const map = new Map();
(stories || []).forEach((story) => {
if (!story) return;
addStoryKey(map, story.id, story);
addStoryKey(map, story.email, story);
addStoryKey(map, story.learnerId, story);
addStoryKey(map, story.uid, story);
addStoryKey(map, story.userId, story);
addStoryKey(map, story.docId, story);
addStoryKey(map, story.userEmail, story);
addStoryKey(map, story.learnerEmail, story);
});
return map;
}

async function readDoc(systemName, uniqueId) {
if (!uniqueId) return null;
try {
const read = httpsCallable(getFunctions(), "firestoreReadDocSafe");
const res = await read({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName, uniqueId, auth: true });
return res?.data?.found && res?.data?.data ? res.data.data : null;
} catch {
return null;
}
}

async function findApplicantUid(email) {
const safeEmail = String(email || "").trim().toLowerCase();
if (!safeEmail) return "";
const applicant = await readDoc("Applicants", safeEmail);
return applicant?.uid || applicant?.firebaseUid || applicant?.userId || applicant?.firebase_uid || applicant?.authUid || "";
}

async function findStudentStory(student) {
const email = String(student?.email || "").trim().toLowerCase();
try {
const gsd = httpsCallable(getFunctions(), "getSystemDocuments");
const storyRes = await gsd({ company: COMPANY, collectionBrand: COLLECTION_BRAND, systemName: "learnerAssessmentStory", auth: true });
const storyMap = buildStoryMap(rowArray(storyRes));
const candidates = [
email,
student?.learnerId,
student?.uid,
student?.userId,
student?.studentNumber,
].map((key) => String(key || "").trim().toLowerCase()).filter(Boolean);
for (const key of candidates) {
const story = storyMap.get(key);
if (story) return story;
}
} catch (err) {
console.warn("[StudentProgressModal] bulk story lookup failed:", err?.message);
}

const uid = await findApplicantUid(email);
const docIdsToTry = [
uid,
email,
email.replace("@", "_at_").replace(/\./g, "_"),
].filter(Boolean);

for (const docId of docIdsToTry) {
const story = await readDoc("learnerAssessmentStory", docId);
if (story) return story;
}
return null;
}

function resolveHistory(story) {
if (!story) return [];
const candidates = [story.complexityHistory, story.data?.complexityHistory, story.assessmentHistory, story.history, story.grades];
for (const c of candidates) {
if (Array.isArray(c) && c.length > 0 && c[0]?.assessment != null) return c;
}
for (const key of Object.keys(story)) {
const val = story[key];
if (Array.isArray(val) && val.length > 0 && val[0]?.assessment != null) return val;
if (val && typeof val === "object" && !Array.isArray(val)) {
for (const innerKey of Object.keys(val)) {
const inner = val[innerKey];
if (Array.isArray(inner) && inner.length > 0 && inner[0]?.assessment != null) return inner;
}
}
}
return [];
}

function pctFromEntry(e) {
return e.assessment?.percent ?? (e.assessment?.maxScore > 0 ? (100 * (e.assessment?.score || 0)) / e.assessment.maxScore : 0);
}

function buildUnitProgressRows(programStructure, byUnit, courseName) {
if (!programStructure) return [];
const rows = [];
const seenUnits = new Set();
const addUnitRow = (unitName, moduleName = "", componentName = "") => {
if (!unitName || seenUnits.has(unitName)) return;
seenUnits.add(unitName);
const progress = byUnit?.[unitName] || { currentAvg: 0, written: 0, total: 0, cnycStatus: "NYC-NS" };
rows.push({
courseName,
moduleName,
componentName,
unitName,
currentAvg: progress.currentAvg || 0,
written: progress.written || 0,
total: progress.total || 0,
belowPassCount: progress.belowPassCount || 0,
cnycStatus: progress.cnycStatus || "NYC-NS",
});
};
(programStructure.modules || []).forEach((mod) => {
const moduleUnits = [];
(mod.components || []).forEach((compName) => {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
normalizeUnitRefs(comp?.units).forEach((unitRef) => {
moduleUnits.push({ unitRef, componentName: getName(comp) || getName(compName) });
});
});
normalizeUnitRefs(mod.units).forEach((unitRef) => moduleUnits.push({ unitRef, componentName: "" }));
moduleUnits.forEach(({ unitRef, componentName }) => {
const unit = typeof unitRef === "object" ? unitRef : getUnitByName(programStructure, unitRef);
const unitName = getUnitName(unit, unitRef);
addUnitRow(unitName, mod.name, componentName);
});
});
normalizeUnitRefs(programStructure.units).forEach((unitRef) => {
const unitName = getUnitName(unitRef, unitRef);
const path = resolvePathForUnit(programStructure, unitName);
addUnitRow(unitName, path.moduleName, path.componentName);
});
return rows;
}

function countProgrammeUnits(programStructure) {
return getProgrammeUnitNames(programStructure).length;
}

function buildLearnerLookupIds(student) {
return Array.from(new Set([
student?.email,
student?.uid,
student?.firebaseUid,
student?.userId,
student?.learnerId,
student?.studentNumber,
].map((value) => String(value || "").trim()).filter(Boolean)));
}

function buildProgrammeUnitIds(programStructure, programmeId) {
return getProgrammeUnitNames(programStructure)
.map((unitName) => {
const unit = getUnitByName(programStructure, unitName);
return String(unit?.unitId || unit?.id || "").trim() || buildUnitId(unitName, programmeId, COMPANY);
})
.filter(Boolean);
}

function summarizeLearningProgressUnits(unitsMap, expectedUnitIds = []) {
const expected = new Set((expectedUnitIds || []).map((id) => String(id || "").trim()).filter(Boolean));
const rows = Object.entries(unitsMap || {}).filter(([unitId]) => !expected.size || expected.has(String(unitId || "").trim()));
const total = expected.size || rows.length;
const done = rows.filter(([, unit]) => isLearningUnitComplete(unit)).length;
return { total, done };
}

function isLearningUnitComplete(unit) {
const total = Number(unit?.totalAssessments ?? unit?.total ?? unit?.assessmentTotal ?? unit?.requiredAssessments ?? 0);
const completed = Number(unit?.completedAssessments ?? unit?.written ?? unit?.completed ?? unit?.assessmentsCompleted ?? 0);
const percent = Number(unit?.progressPercent ?? unit?.percent ?? unit?.progress ?? 0);
if (total > 0) return completed >= total;
return percent >= 100 || unit?.status === "completed" || unit?.completed === true;
}

function selectBestProgramStructure(programmeSources) {
return (programmeSources || [])
.filter(Boolean)
.sort((a, b) => countProgrammeUnits(b) - countProgrammeUnits(a))[0] || null;
}

function getProgrammeUnitNames(programStructure) {
if (!programStructure) return [];
const units = new Set();
(programStructure.modules || []).forEach((mod) => {
(mod.components || []).forEach((compName) => {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
normalizeUnitRefs(comp?.units).forEach((unitRef) => {
const unit = typeof unitRef === "object" ? unitRef : getUnitByName(programStructure, unitRef);
const unitName = getUnitName(unit, unitRef);
if (unitName) units.add(unitName);
});
});
normalizeUnitRefs(mod.units).forEach((unitRef) => {
const unit = typeof unitRef === "object" ? unitRef : getUnitByName(programStructure, unitRef);
const unitName = getUnitName(unit, unitRef);
if (unitName) units.add(unitName);
});
});
normalizeUnitRefs(programStructure.units).forEach((unitRef) => {
const unitName = getUnitName(unitRef, unitRef);
if (unitName) units.add(unitName);
});
return Array.from(units);
}

function resolvePathForUnit(programStructure, unitName) {
const normalizedUnit = normalizeTitle(unitName);
for (const mod of programStructure?.modules || []) {
const moduleUnits = [];
(mod.components || []).forEach((compName) => {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
normalizeUnitRefs(comp?.units).forEach((unitRef) => {
moduleUnits.push({ unitRef, componentName: getName(comp) || getName(compName) });
});
});
normalizeUnitRefs(mod.units).forEach((unitRef) => moduleUnits.push({ unitRef, componentName: "" }));
const hit = moduleUnits.find(({ unitRef }) => {
const unit = typeof unitRef === "object" ? unitRef : getUnitByName(programStructure, unitRef);
return normalizeTitle(getUnitName(unit, unitRef)) === normalizedUnit;
});
if (hit) return { moduleName: mod.name || "", componentName: hit.componentName || "" };
}
return { moduleName: "", componentName: "" };
}

async function fetchUnitAssessmentsForProgram(programStructure, programId) {
if (programId && unitAssessmentCache.has
(programId)) return unitAssessmentCache.get(programId);
const promise = (async () => {
const unitsToFetch = [];
for (const mod of programStructure?.modules || []) {
for (const compName of mod.components || []) {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
for (const unitName of normalizeUnitRefs(comp?.units)) {
if (!unitsToFetch.includes(unitName)) unitsToFetch.push(unitName);
}
}
}
const cache = {};
const batchSize = 3;
for (let i = 0; i < unitsToFetch.length; i += batchSize) {
await Promise.all(unitsToFetch.slice(i, i + batchSize).map(async (unitName) => {
const unitId = buildUnitId(unitName, programId, COMPANY);
try {
const res = await fetchAIContentBuilderData(programId, unitId, COMPANY, COLLECTION_BRAND);
if (!res?.success || !res?.data?.unit?.outcomes) {
    cache[unitName] = { assessments: [] };
    return;
}
const assessments = [];
for (const outcome of res.data.unit.outcomes || []) {
    for (const sec of outcome.outline?.sections || []) {
    const hasAssessment = sec.complexities?.assessment && (Array.isArray(sec.complexities.assessment) ? sec.complexities.assessment.length > 0 : true);
    if (hasAssessment) assessments.push({ sectionTitle: sec.title || "Assessment", outlineName: sec.title || "Assessment", outcomeName: outcome.name || "", unitName });
    }
}
cache[unitName] = { assessments, outcomes: res.data.unit.outcomes || [] };
} catch {
cache[unitName] = { assessments: [], outcomes: [] };
}
}));
}
return cache;
})();
if (programId) unitAssessmentCache.set(programId, promise);
return promise;
}

function buildHistoryPercentMap(history) {
const map = new Map();
(history || []).forEach((entry) => {
if (!entry?.assessment) return;
const keys = [
entry.sectionTitle,
entry.outcomeName,
entry.assessment?.title,
entry.assessment?.name,
].map(normalizeTitle).filter(Boolean);
const percent = pctFromEntry(entry);
keys.forEach((key) => {
map.set(key, Math.max(map.get(key) ?? 0, percent));
});
});
return map;
}

function findAssessmentPercent(assessment, historyMap) {
const keys = [
assessment?.sectionTitle,
assessment?.outlineName,
assessment?.outcomeName,
`${assessment?.outcomeName || ""} ${assessment?.outlineName || assessment?.sectionTitle || ""}`,
].map(normalizeTitle).filter(Boolean);
for (const key of keys) {
if (historyMap.has(key)) return historyMap.get(key);
}
return null;
}

function buildOutcomeOutlineRows(programStructure, unitAssessmentsCache, history, byUnit, snapshotGrades = new Map()) {
if (!programStructure?.modules?.length) return [];
const historyMap = buildHistoryPercentMap(history);
// Merge learnerAssessments scores into the history map
snapshotGrades.forEach((pct, key) => {
const existing = historyMap.get(key);
if (existing == null || pct > existing) historyMap.set(key, pct);
});
const rows = [];
(programStructure.modules || []).forEach((mod) => {
(mod.components || []).forEach((compName) => {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
const componentName = getName(comp) || getName(compName);
normalizeUnitRefs(comp?.units).forEach((unitRef) => {
const unit = typeof unitRef === "object" ? unitRef : getUnitByName(programStructure, unitRef);
const unitName = getUnitName(unit, unitRef);
const cached = unitAssessmentsCache?.[unitRef] || unitAssessmentsCache?.[unitName] || {};
let assessments = Array.isArray(cached.assessments) ? cached.assessments : [];
if (!assessments.length) {
    assessments = collectionItems(unit?.outcomes).map((outcome) => ({
    outcomeName: typeof outcome === "string" ? outcome : outcome?.name || outcome?.description || "Outcome",
    outlineName: "Assessment",
    sectionTitle: typeof outcome === "string" ? outcome : outcome?.name || outcome?.description || "Assessment",
    unitName,
    }));
}
assessments.forEach((assessment) => {
    const percent = findAssessmentPercent(assessment, historyMap);
    rows.push({
    moduleName: mod.name,
    componentName,
    unitName,
    outcomeName: assessment.outcomeName || "Outcome",
    outlineName: assessment.outlineName || assessment.sectionTitle || "Assessment",
    currentAvg: percent == null ? 0 : Math.round(percent),
    written: percent == null ? 0 : 1,
    total: 1,
    unitCurrentAvg: byUnit?.[unitName]?.currentAvg || 0,
    });
});
});
});
});
return rows;
}

function buildResubmissionRows(outcomeOutlineRows, failedAssessments, passRate) {
const rows = [];
const seen = new Set();
(outcomeOutlineRows || []).forEach((row) => {
if (!row.written || Number(row.currentAvg || 0) >= passRate) return;
const key = `${row.unitName || ""}|${row.outlineName || ""}|${row.currentAvg}`;
if (seen.has(key)) return;
seen.add(key);
rows.push({
moduleName: row.moduleName || "",
componentName: row.componentName || "",
unitName: row.unitName || "",
outcomeName: row.outcomeName || "",
outlineName: row.outlineName || "Assessment",
name: row.outlineName || row.outcomeName || "Assessment",
score: Math.round(row.currentAvg || 0),
passRate,
});
});
(failedAssessments || []).forEach((item) => {
const key = `${item.unitName || ""}|${item.name || ""}|${item.score}`;
if (seen.has(key)) return;
seen.add(key);
rows.push({
moduleName: item.moduleName || "",
componentName: item.componentName || "",
unitName: item.unitName || "",
outcomeName: item.outcomeName || "",
outlineName: item.outlineName || item.name || "Assessment",
name: item.name || "Assessment",
score: Math.round(item.score || 0),
passRate: item.passRate || passRate,
});
});
return rows.sort((a, b) => (a.unitName || "").localeCompare(b.unitName || "") || (a.name || "").localeCompare(b.name || ""));
}

const normalizeTitle = (t) => (!t || typeof t !== "string") ? "" : t.replace(/^\s*\d+\.?\s*/, "").trim().replace(/\s+/g, " ").toLowerCase();
const collectionItems = (value) => {
if (Array.isArray(value)) return value;
if (value && typeof value === "object") return Object.values(value);
return [];
};
const normalizeUnitRefs = (units) => collectionItems(units)
.map((unit) => (unit && typeof unit === "object" ? (unit.name || unit.unitName || unit.id || unit.uid || unit.title || unit) : unit))
.filter(Boolean);
const getName = (value) => String(value?.name || value?.unitName || value?.componentName || value?.id || value?.uid || value?.title || value || "").trim();
const findByName = (items, name) => {
const target = normalizeTitle(getName(name));
if (!target) return null;
return collectionItems(items).find((item) => normalizeTitle(getName(item)) === target) || null;
};
const getComponentByName = (pd, name) => findByName(pd?.components, name);
const getUnitByName = (pd, name) => findByName(pd?.units, name);
const getUnitName = (unit, fallback) => getName(unit) || getName(fallback);

function buildOutcomeToUnitMap(pd) {
const map = new Map();
collectionItems(pd?.units).forEach((unit) => {
(collectionItems(unit.outcomes)).forEach((outcome) => {
const key = normalizeTitle(typeof outcome === "string" ? outcome : outcome?.name || outcome?.description || "");
if (key) map.set(key, { unitName: getUnitName(unit, unit) });
});
});
return map;
}

function buildUnitLookup(pd) {
const lookup = new Map();
for (const mod of pd?.modules || []) {
for (const compName of mod.components || []) {
const comp = getComponentByName(pd, compName);
for (const unitName of normalizeUnitRefs(comp?.units)) {
const unit = getUnitByName(pd, unitName);
const resolvedName = getUnitName(unit, unitName);
if (resolvedName) lookup.set(normalizeTitle(resolvedName), { unitRef: unit || { name: resolvedName }, componentRef: comp, moduleRef: mod });
}
}
}
return lookup;
}

function tryLooseMatch(norm, map) {
const loose = norm.replace(/[^a-z0-9\s]/g, "").trim();
for (const [key, value] of map) {
const candidate = key.replace(/[^a-z0-9\s]/g, "").trim();
if (candidate === loose || candidate.includes(loose) || loose.includes(candidate)) return value;
}
return null;
}

function tryTokenMatch(norm, map) {
const tokens = norm.split(/\s+/).filter((token) => token.length > 2);
if (tokens.length < 2) return null;
let best = null;
let score = 0;
for (const [key, value] of map) {
const keyTokens = key.split(/\s+/).filter((token) => token.length > 2);
const overlap = tokens.filter((token) => keyTokens.some((keyToken) => keyToken.includes(token) || token.includes(keyToken))).length;
if (overlap >= 2 && overlap > score) {
score = overlap;
best = value;
}
}
return best;
}

function mapEntryToStructure(entry, pd) {
if (!pd || !entry?.assessment) return null;
const outcomeToUnit = buildOutcomeToUnitMap(pd);
const unitLookup = buildUnitLookup(pd);
const norm = normalizeTitle(entry.sectionTitle || entry.outcomeName || "");
const hit = outcomeToUnit.get(norm) || tryLooseMatch(norm, outcomeToUnit) || tryTokenMatch(norm, outcomeToUnit);
if (hit) {
const resolved = unitLookup.get(normalizeTitle(hit.unitName));
if (resolved) return { unit: resolved.unitRef?.name || null };
}
if (entry.sectionTitle) {
let bestScore = 0;
let bestMatch = null;
unitLookup.forEach((value, key) => {
const overlap = key.split(" ").filter(Boolean).filter((token) => normalizeTitle(entry.sectionTitle).includes(token)).length;
if (overlap > bestScore) {
bestScore = overlap;
bestMatch = value;
}
});
if (bestScore >= 2) return { unit: bestMatch?.unitRef?.name || null };
}
return null;
}


// ── buildGradeMapFromSnapshot ─────────────────────────────────────────────────
// Reads scores from the learnerAssessments snapshot response.
// Firebase outline keys use underscores (e.g. Field_Execution_of_Core_Survey_Tasks)
// — they are converted to spaces then normalised with normalizeTitle() so they
// match the keys already in lookupGradeMap (built from assessmentStory).
function buildGradeMapFromSnapshot(learningProgressUnits) {
const gradeMap = new Map();
if (!learningProgressUnits || typeof learningProgressUnits !== "object") return gradeMap;
const setBest = (key, pct) => {
const normKey = normalizeTitle(key);
if (!normKey || pct == null) return;
const existing = gradeMap.get(normKey);
if (existing == null || pct > existing) gradeMap.set(normKey, pct);
};
for (const unitNode of Object.values(learningProgressUnits)) {
if (!unitNode || typeof unitNode !== "object") continue;
const outcomes = unitNode?.outcomes || {};
Object.entries(outcomes).forEach(([outcomeName, itemsRaw]) => {
    const items = Array.isArray(itemsRaw) ? itemsRaw : [];
    items.forEach((item) => {
    if (!item?.hasAssessment || !item?.attempt) return;
    const attempt = item.attempt || {};
    const nestedAssessment = attempt?.assessment && typeof attempt.assessment === "object" ? attempt.assessment : {};
    const score = Number(attempt.score ?? nestedAssessment.score ?? 0);
    const maxScore = Number(attempt.maxScore ?? nestedAssessment.maxScore ?? 10);
    const pct = attempt.percent ?? nestedAssessment.percent ?? (score > 0 && maxScore > 0 ? Math.round((score / maxScore) * 100) : null);
    if (pct == null) return;
    const title = String(attempt.sectionTitle || item.title || "").replace(/\s*\(Assessment\)\s*$/i, "").trim();
    setBest(title, pct);
    setBest(outcomeName, pct);
    setBest(`${outcomeName} ${title}`, pct);
    setBest(attempt.outcomeName, pct);
    setBest(`${attempt.outcomeName || ""} ${title}`, pct);
    });
});
const summary = unitNode?.summary || {};
for (const [outlineKey, entry] of Object.entries(summary)) {
    if (!entry || typeof entry !== "object") continue;
    const nestedAssessment = entry?.assessment && typeof entry.assessment === "object" ? entry.assessment : {};
    const score    = Number(entry.score ?? nestedAssessment.score ?? 0);
    const maxScore = Number(entry.maxScore ?? nestedAssessment.maxScore ?? 10);
    const pct = entry.percent ?? nestedAssessment.percent ?? (score > 0 && maxScore > 0 ? Math.round((score / maxScore) * 100) : null);
    if (pct == null) continue;
    const cleanKey = outlineKey.replace(/_+/g, " ").trim();
    setBest(cleanKey, pct);
    setBest(entry.sectionTitle || entry.title, pct);
    setBest(entry.outcomeName, pct);
    setBest(`${entry.outcomeName || ""} ${entry.sectionTitle || entry.title || cleanKey}`, pct);
}
}
return gradeMap;
}

function buildHistoryFromSnapshot(learningProgressUnits) {
const history = [];
Object.values(learningProgressUnits || {}).forEach((unitNode) => {
Object.entries(unitNode?.outcomes || {}).forEach(([outcomeName, itemsRaw]) => {
(Array.isArray(itemsRaw) ? itemsRaw : []).forEach((item) => {
if (!item?.hasAssessment || !item?.attempt) return;
const attempt = item.attempt || {};
const score = Number(attempt.score);
const maxScore = Number(attempt.maxScore);
const storedPercent = Number(attempt.percent);
const percent = attempt.percent != null && Number.isFinite(storedPercent)
    ? storedPercent
    : Number.isFinite(score) && Number.isFinite(maxScore) && maxScore > 0
        ? Math.round((score / maxScore) * 100)
        : null;
if (percent == null && !attempt.completed && !attempt.completedAt) return;
const sectionTitle = String(attempt.sectionTitle || item.title || "")
    .replace(/\s*\(Assessment\)\s*$/i, "")
    .trim();
history.push({
    assessment: {
    score: Number.isFinite(score) ? score : null,
    maxScore: Number.isFinite(maxScore) && maxScore > 0 ? maxScore : null,
    percent: percent == null ? null : Math.max(0, Math.min(100, percent)),
    },
    completed: true,
    completedAt: attempt.completedAt || null,
    sectionTitle,
    assessmentName: sectionTitle,
    outcomeName: attempt.outcomeName || outcomeName,
    unitName: attempt.unitName || unitNode?.unitName || "",
});
});
});
});
return history;
}

function buildAssessmentCacheFromSnapshot(learningProgressUnits) {
const cache = {};
Object.entries(learningProgressUnits || {}).forEach(([unitKey, unitNode]) => {
const unitName = unitNode?.unitName || unitKey;
const assessments = [];
Object.entries(unitNode?.outcomes || {}).forEach(([outcomeName, itemsRaw]) => {
(Array.isArray(itemsRaw) ? itemsRaw : []).forEach((item) => {
if (!item?.hasAssessment) return;
const title = String(item?.title || "Assessment").replace(/\s*\(Assessment\)\s*$/i, "").trim();
assessments.push({ sectionTitle: title, assessmentName: title, outcomeName, unitName });
});
});
cache[unitName] = { assessments };
if (unitNode?.unitId) cache[unitNode.unitId] = { assessments };
});
return cache;
}

function buildStudentAggregatesWithContent(student, programStructure, unitAssessmentsCache, snapshotGrades = new Map()) {
if (!programStructure?.modules || !student) return null;
const history = resolveHistory(student);
const lookupGradeMap = new Map();
const setBestGrade = (key, percent) => {
const normalized = normalizeTitle(key || "");
if (!normalized || !Number.isFinite(Number(percent))) return;
lookupGradeMap.set(normalized, Math.max(lookupGradeMap.get(normalized) ?? 0, Number(percent)));
};
history.forEach((entry) => {
if (!entry?.assessment) return;
const percent = pctFromEntry(entry);
setBestGrade(entry.sectionTitle, percent);
setBestGrade(entry.assessmentName, percent);
setBestGrade(entry.outcomeName, percent);
setBestGrade(entry.assessment?.title, percent);
setBestGrade(entry.assessment?.name, percent);
setBestGrade(`${entry.outcomeName || ""} ${entry.sectionTitle || entry.assessmentName || entry.assessment?.title || entry.assessment?.name || ""}`, percent);
});
// Merge learnerAssessments scores — authoritative source, takes higher score
snapshotGrades.forEach((pct, key) => {
const existing = lookupGradeMap.get(key);
if (existing == null || pct > existing) lookupGradeMap.set(key, pct);
});

const byUnit = {};
for (const mod of programStructure.modules || []) {
for (const compName of mod.components || []) {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
for (const unitRef of normalizeUnitRefs(comp?.units)) {
const unit = typeof unitRef === "object" ? unitRef : getUnitByName(programStructure, unitRef);
const unitKey = getUnitName(unit, unitRef);
const content = unitAssessmentsCache[unitRef]?.assessments || unitAssessmentsCache[unitKey]?.assessments || [];
let total = content.length;
let written = 0;
let sumAvg = 0;
let belowPassCount = 0;
content.forEach((assessment) => {
    const keys = [
    assessment.sectionTitle,
    assessment.outlineName,
    assessment.assessmentName,
    assessment.outcomeName,
    `${assessment.outcomeName || ""} ${assessment.outlineName || assessment.sectionTitle || assessment.assessmentName || ""}`,
    ].map(normalizeTitle).filter(Boolean);
    const percent = keys.map((key) => lookupGradeMap.get(key)).find((value) => value != null) ?? null;
    if (percent != null) written += 1;
    if (percent != null && percent < getPR(programStructure)) belowPassCount += 1;
    sumAvg += percent ?? 0;
});
const fallbackMatched = history.filter((entry) => {
    if (!entry?.assessment) return false;
    const mapped = mapEntryToStructure(entry, programStructure);
    return mapped?.unit === unitKey;
});
const fallbackByAssessment = new Map();
fallbackMatched.forEach((entry) => {
    const key = normalizeTitle(entry.sectionTitle || entry.outcomeName || "");
    if (!key) return;
    const percent = pctFromEntry(entry);
    fallbackByAssessment.set(key, Math.max(fallbackByAssessment.get(key) ?? 0, percent));
});
const fallbackScores = Array.from(fallbackByAssessment.values());
if (fallbackScores.length > written) {
    total = content.length > 0 ? content.length : fallbackScores.length;
    written = capProgress(fallbackScores.length, total);
    const usedScores = fallbackScores.slice(0, written);
    sumAvg = usedScores.reduce((sum, percent) => sum + percent, 0);
    belowPassCount = usedScores.filter((percent) => percent < getPR(programStructure)).length;
} else if (written === 0 && content.length === 0 && fallbackMatched.length > 0) {
    total = fallbackScores.length;
    written = fallbackScores.length;
    sumAvg = fallbackScores.reduce((sum, percent) => sum + percent, 0);
    belowPassCount = fallbackScores.filter((percent) => percent < getPR(programStructure)).length;
}
written = capProgress(written, total);
byUnit[unitKey] = {
    total,
    written,
    belowPassCount,
    rawSum: sumAvg,
    avg: total > 0 ? Math.min(100, Math.round(sumAvg / total)) : 0,
    currentAvg: written > 0 ? Math.min(100, Math.round(sumAvg / written)) : 0,
};
}
}
}

const byComponent = {};
for (const mod of programStructure.modules || []) {
for (const compName of mod.components || []) {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
const compKey = getName(comp) || getName(compName);
let total = 0;
let written = 0;
let sumAvg = 0;
let rawSum = 0;
let belowPassCount = 0;
for (const unitRef of normalizeUnitRefs(comp?.units)) {
const unit = byUnit[(getUnitName(getUnitByName(programStructure, unitRef), unitRef))];
if (unit) {
    total += unit.total;
    written += unit.written;
    sumAvg += unit.total > 0 ? unit.avg * unit.total : 0;
    rawSum += unit.rawSum || 0;
    belowPassCount += unit.belowPassCount || 0;
}
}
written = capProgress(written, total);
byComponent[compKey] = {
total,
written,
belowPassCount,
rawSum,
avg: total > 0 ? Math.min(100, Math.round(sumAvg / total)) : 0,
currentAvg: written > 0 ? Math.min(100, Math.round(rawSum / written)) : 0,
};
}
}

const byModule = {};
for (const mod of programStructure.modules || []) {
let total = 0;
let written = 0;
let sumAvg = 0;
let rawSum = 0;
let belowPassCount = 0;
for (const compName of mod.components || []) {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
const component = byComponent[getName(comp) || getName(compName)];
if (component) {
total += component.total;
written += component.written;
sumAvg += component.avg * component.total;
rawSum += component.rawSum || 0;
belowPassCount += component.belowPassCount || 0;
}
}
written = capProgress(written, total);
const units = [];
(mod.components || []).forEach((compName) => {
const comp = typeof compName === "object" ? compName : getComponentByName(programStructure, compName);
normalizeUnitRefs(comp?.units).forEach((unitRef) => {
const unit = typeof unitRef === "object" ? unitRef : getUnitByName(programStructure, unitRef);
const unitName = getUnitName(unit, unitRef);
if (unitName) units.push(unitName);
});
});
byModule[mod.name] = {
total,
written,
belowPassCount,
rawSum,
units,
avg: total > 0 ? Math.min(100, Math.round(sumAvg / total)) : 0,
currentAvg: written > 0 ? Math.min(100, Math.round(rawSum / written)) : 0,
};
}

const passRate = getPR(programStructure);
Object.keys(byUnit).forEach((key) => {
const unit = byUnit[key];
unit.cnycStatus = getCNYC(unit.written, unit.total, unit.currentAvg, passRate, unit.belowPassCount || 0);
});
Object.keys(byComponent).forEach((key) => {
const component = byComponent[key];
component.cnycStatus = getCNYC(component.written, component.total, component.currentAvg, passRate, component.belowPassCount || 0);
});
Object.keys(byModule).forEach((key) => {
const module = byModule[key];
module.cnycStatus = getCNYC(module.written, module.total, module.currentAvg, passRate, module.belowPassCount || 0);
});

const failedAssessmentMap = new Map();
history.forEach((entry) => {
if (!entry?.assessment) return;
const name = entry.sectionTitle || entry.outcomeName || "";
if (!name) return;
const percent = pctFromEntry(entry);
const mapped = mapEntryToStructure(entry, programStructure);
const path = resolvePathForUnit(programStructure, mapped?.unit || "");
const key = normalizeTitle(`${mapped?.unit || ""} ${name}`);
const existing = failedAssessmentMap.get(key);
if (!existing || percent > existing.score) {
failedAssessmentMap.set(key, {
moduleName: path.moduleName || "",
componentName: path.componentName || "",
unitName: mapped?.unit || "",
outcomeName: entry.outcomeName || "",
outlineName: entry.sectionTitle || entry.assessment?.title || entry.assessment?.name || name,
name,
score: Math.round(percent),
passRate,
});
}
});
const failedAssessments = Array.from(failedAssessmentMap.values()).filter((item) => item.score > 0 && item.score < passRate);

return { byModule, byComponent, byUnit, passRate, failedAssessments };
}
